import { ReactElement, useEffect } from 'react'
import { useQuery, gql, NetworkStatus } from '@apollo/client'
import { isEmpty } from 'lodash'
import { FormattedMessage, defineMessages, useIntl } from 'react-intl'
import { NavLink, useLocation } from 'react-router-dom'

import { Button } from 'components/v2/Button'
import EmptyState from 'components/v2/EmptyState'
import Heading from 'components/v2/Heading'
import { Loader } from '@10xLabs/react-web/components/Loader'
import { Actions, Content, Header } from 'components/Tabs'
import { ForbiddenView } from 'features/App/components/ForbiddenView'
import { withLastUrl } from 'features/App/components/WithLastUrl'
import { CouponTabs } from 'features/Coupons/components/Tabs'
import { FEATURES } from 'features/Roles/components/Permissions/constants'
import { getQueries } from 'utils/route'
import { canRead, canWrite } from 'features/Users/utils'
import { SnacksCouponsList } from './List'
import { SnacksCouponsPagination } from './Pagination'
import { GTCampaignArea, GTCouponsWithPaginationInput, GTPagination } from 'gql/types.generated'
import { useLogin } from 'features/Login/hooks'
import { CouponsFilters } from 'features/Coupons/components/Filters'
import { ContentBody } from 'components/v2/Content'

const LIMIT = 30

const messages = defineMessages({
  helmet: {
    id: 'coupons.snacks.helmet',
    defaultMessage: 'Snacks Coupons | Nexbus Admin'
  }
})

const GET_SNACKS_COUPONS = gql`
  query GetSnacksCoupons($pagination: Pagination, $input: CouponsWithPaginationInput) {
    couponsWithPagination(pagination: $pagination, input: $input) {
      total
      items {
        id
        code
        campaign {
          id
          name
          company {
            id
            name
          }
        }
        startsAt
        endsAt
        isActive
        discountType
        value
        usedCount
        constraints {
          limit
          minSale
        }
      }
    }
  }
`

const SnacksCouponsView = (): ReactElement => {
  const location = useLocation()
  let content
  const intl = useIntl()
  const login = useLogin()
  const hasAccess = canRead(FEATURES.CAMPAIGN, login.accessToken.policies)

  const searchQuery = getQueries(location.search)
  const campaignId = searchQuery.campaignId
  const page = parseInt(searchQuery.page || '1')

  const searchFilters = {
    code: searchQuery.code
  }

  let variables: { input: GTCouponsWithPaginationInput; pagination: GTPagination } = {
    input: {
      campaignAreas: [GTCampaignArea.Snacks],
      code: searchFilters.code
    },
    pagination: {
      size: LIMIT,
      page: page - 1
    }
  }

  if (campaignId) {
    variables = {
      ...variables,
      input: {
        ...variables.input,
        campaignID: campaignId
      }
    }
  }
  const {
    loading,
    error,
    data = {},
    refetch,
    networkStatus
  } = useQuery(GET_SNACKS_COUPONS, {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
    variables,
    skip: !hasAccess
  })

  useEffect(() => {
    document.title = intl.formatMessage(messages.helmet)

    if (hasAccess) {
      refetch()
    }
  }, [])

  if (!hasAccess) {
    return <ForbiddenView feature={FEATURES.CAMPAIGN} />
  }
  if (networkStatus === NetworkStatus.refetch || loading) {
    content = (
      <EmptyState.Container>
        <Loader />
      </EmptyState.Container>
    )
  } else if (error) {
    content = (
      <EmptyState.Container>
        <EmptyState.Description>
          <FormattedMessage
            id="coupons.snacks.error.description"
            defaultMessage="There was an error while loading your coupons"
          />
        </EmptyState.Description>

        <EmptyState.Action>
          <Button onClick={(): TSFixMe => refetch()} kind="primary">
            <FormattedMessage id="coupons.snacks.error.action" defaultMessage="Reload your coupons" />
          </Button>
        </EmptyState.Action>
      </EmptyState.Container>
    )
  } else if (isEmpty(data.couponsWithPagination.items) && page === 1) {
    content = (
      <EmptyState.Container>
        <EmptyState.Description>
          <FormattedMessage id="coupons.snacks.empty.description" defaultMessage={"You don't have any coupon yet"} />
        </EmptyState.Description>

        {canWrite(FEATURES.CAMPAIGN, login.accessToken.policies) && (
          <EmptyState.Action>
            <Button as={NavLink} to="/coupons/snacks/new" kind="primary">
              <FormattedMessage id="coupons.snacks.empty.action" defaultMessage="Add your first coupon" />
            </Button>
          </EmptyState.Action>
        )}
      </EmptyState.Container>
    )
  } else if (isEmpty(data.couponsWithPagination.items)) {
    content = (
      <EmptyState.Container>
        <EmptyState.Description>
          <FormattedMessage
            id="coupons.snacks.empty.filters.description"
            defaultMessage="There was no coupons found according to your filters"
          />
        </EmptyState.Description>

        <EmptyState.Action>
          <Button as={NavLink} to="/coupons/snacks" kind="primary">
            <FormattedMessage id="coupons.snacks.empty.filters.action" defaultMessage="Reset filters" />
          </Button>
        </EmptyState.Action>
      </EmptyState.Container>
    )
  } else {
    content = (
      <>
        <SnacksCouponsList coupons={data.couponsWithPagination.items} />

        <SnacksCouponsPagination
          page={page}
          limit={LIMIT}
          totalItems={data.couponsWithPagination.total}
          currentItemsCount={data.couponsWithPagination.items.length}
          filters={{
            campaignId
          }}
        />
      </>
    )
  }

  return (
    <>
      <Header>
        <Heading.H3>
          <FormattedMessage id="coupons.snacks.header.label" defaultMessage="Coupons list" />
        </Heading.H3>
      </Header>

      <CouponTabs />

      <Actions>
        {canWrite(FEATURES.CAMPAIGN, login.accessToken.policies) && (
          <Button as={NavLink} to="/coupons/snacks/new" kind="primary">
            <FormattedMessage id="coupons.header.new" defaultMessage="+ Add coupon" />
          </Button>
        )}
      </Actions>

      <Content>
        <ContentBody>
          <CouponsFilters filters={searchFilters} />
          {content}
        </ContentBody>
      </Content>
    </>
  )
}

export default withLastUrl(SnacksCouponsView)
